import * as THREE from 'three';

/**
 * Sunięcie po **całej trasie**, własnym zegarem.
 *
 * # Dlaczego to w ogóle istnieje
 *
 * Pionek i statek szły dotąd **od wiadomości do wiadomości**: serwer mówił „jest
 * na tym polu", a klient wygładzał przejście do niego i **czekał** na następną.
 * Wiadomości nie przychodzą równo — jedna spóźni się o trzydzieści milisekund,
 * druga przyjdzie razem z następną — więc wygładzenie kończyło się przed czasem
 * i bryła stawała na każdym polu. Im dłuższy krok, tym wyraźniej: przy statku
 * było to widać jako szarpanie.
 *
 * **Trasę znamy z góry**, bo serwer ją odsyła (`route`), a reguły ruchu są po
 * tej stronie bliźniakami jego reguł. Nie ma więc na co czekać: bryła idzie
 * swoim torem, a wiadomości są **potwierdzeniem, nie zezwoleniem**.
 *
 * # Co to zmienia, a czego nie
 *
 * Zmienia **wyłącznie obraz**. `standing` — czyli to, od czego liczy się zasięg
 * budowy, panel pola i zbiór — dalej zmienia się dopiero na wiadomość z serwera,
 * więc nic nie zaproponuje akcji, której serwer odmówi. Różnica między obrazem
 * a regułami to najwyżej jeden krok.
 *
 * # Skąd kształt
 *
 * **Ta sama krzywa, którą rysuje się trasa** — centripetal Catmull-Rom przez
 * środki pól (`RouteLine`). Przy sunięciu po całej trasie znamy następne pola,
 * więc nie trzeba już przybliżać zakrętu kierunkiem wejścia: bryła idzie
 * dokładnie po linii, którą widać.
 *
 * Czas idzie **na odcinek, nie na długość łuku**: każdy odcinek trwa tyle, ile
 * jego krok na serwerze (las wolniej, droga szybciej), a bryła mija środek pola
 * dokładnie wtedy, gdy serwer ją tam stawia.
 */
export class Glide {
  private curve?: THREE.CatmullRomCurve3;
  /** Ile trwa każdy odcinek, w sekundach. */
  private legs: number[] = [];
  private leg = 0;
  private elapsed = 0;
  /** Kiedy tor ruszył i ile miał trwać — do porównania z serwerem. */
  private started = 0;
  private planned = 0;
  /** Ile naprawdę trwał; zero, dopóki nie dobiegł końca. */
  private took = 0;

  /**
   * Weź trasę: punkt startowy i środki kolejnych pól, z czasem każdego kroku.
   *
   * Start jest **bieżącym położeniem bryły**, a nie środkiem pola, na którym
   * stoi — inaczej ruszenie z miejsca w trakcie poprzedniego kroku byłoby
   * skokiem.
   */
  follow(from: THREE.Vector3, cells: THREE.Vector3[], seconds: number[]): void {
    if (cells.length === 0 || cells.length !== seconds.length) {
      this.clear();
      return;
    }

    this.curve = new THREE.CatmullRomCurve3([from.clone(), ...cells], false, 'centripetal');
    this.legs = [...seconds];
    this.leg = 0;
    this.elapsed = 0;
    this.started = performance.now();
    this.planned = seconds.reduce((all, one) => all + one, 0);
    this.took = 0;
  }

  clear(): void {
    this.curve = undefined;
    this.legs = [];
    this.leg = 0;
    this.elapsed = 0;
  }

  /** Czy jest po czym sunąć. */
  get running(): boolean {
    return this.curve !== undefined;
  }

  /** Ile tor miał trwać i ile trwał, w sekundach. Zero przed końcem. */
  get timing(): { planned: number; took: number } {
    return { planned: this.planned, took: this.took };
  }

  /** Ile pól trasy zostało przed bryłą. */
  get left(): number {
    return this.curve ? this.legs.length - this.leg : 0;
  }

  /**
   * Przesuń o klatkę. Odpowiada `false`, gdy trasa się skończyła — wtedy
   * wołający zdejmuje glide i wraca do zwykłego wygładzania.
   *
   * **Reszta czasu przechodzi na następny odcinek**, zamiast przepadać na
   * granicy pola. To jest ta jedna linijka, przez którą ruch przestaje stawać:
   * klatka dłuższa od reszty odcinka po prostu wchodzi w następny.
   */
  advance(delta: number): boolean {
    if (!this.curve) return false;

    this.elapsed += delta;
    while (this.elapsed >= this.legs[this.leg]) {
      if (this.leg + 1 >= this.legs.length) {
        this.elapsed = this.legs[this.leg];
        // Mierzone raz, przy pierwszym dobiciu do końca: kolejne klatki pytają
        // o to samo, a czas stoi.
        if (this.took === 0) this.took = (performance.now() - this.started) / 1000;
        return false;
      }
      this.elapsed -= this.legs[this.leg];
      this.leg += 1;
    }
    return true;
  }

  /** Gdzie bryła jest w tej chwili. */
  point(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.curve!.getPoint(this.progress()));
  }

  /** W którą stronę jest zwrócona — styczna krzywej, znormalizowana. */
  tangent(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.curve!.getTangent(this.progress()));
  }

  /**
   * Gdzie na krzywej, w jej własnym parametrze.
   *
   * Odcinek na pole, więc `leg` mówi który, a `elapsed` — ile z niego minęło.
   * Dzięki temu bryła siada na środku pola dokładnie na granicy odcinka, a nie
   * „gdzieś tam", jak wyszłoby z długości łuku.
   */
  private progress(): number {
    const span = this.legs.length;
    const within = this.legs[this.leg] > 0 ? this.elapsed / this.legs[this.leg] : 1;
    return Math.min(1, (this.leg + within) / span);
  }
}
